import { type CarrierName, Currency, type Dtos, formatToPercent } from "@inrev/common";
import { formatMetricCardCurrency } from "../../patterns/metric-card";
import {
	byCarrierPriority,
	type CarrierPriorityInput,
	carrierDisplayName,
	EMPTY_CARRIER_PRIORITIES,
} from "../bonding-line/carrierDisplay";
import {
	type ExposureBreakdownView,
	type ExposureLimitSummary,
	type ExposureSegment,
	type ExposureSegmentColor,
	type ExposureSegmentColumn,
} from "./types";

type SegmentPalette = readonly [ExposureSegmentColor, ...ExposureSegmentColor[]];

/** Each carrier renders in its own brand color rather than an arbitrary slot in a ramp. */
const CARRIER_COLOR_BY_KEY = {
	accelerant: "warning400",
	"lexington-national-for-accelerant": "amber600",
	coaction: "danger700",
} as const satisfies Record<string, ExposureSegmentColor>;

/** A carrier we hold no brand color for cycles a generic ramp rather than borrowing another's identity. */
const UNBRANDED_CARRIER_COLORS = [
	"danger700",
	"warning500",
	"orange600",
] as const satisfies SegmentPalette;

const seriesColor = (palette: SegmentPalette, index: number): ExposureSegmentColor =>
	palette[index % palette.length] ?? palette[0];

const carrierSeriesColor = (key: CarrierName, index: number): ExposureSegmentColor => {
	for (const [carrier, color] of Object.entries(CARRIER_COLOR_BY_KEY)) {
		if (carrier === key) return color;
	}
	return seriesColor(UNBRANDED_CARRIER_COLORS, index);
};

type ProjectedExposure = Dtos.SuretyAccount.Exposure.Projected.Get.Response;

export type ExposureLimitVariant = "admin" | "agent";

export type BuildExposureBreakdownViewsOptions = {
	limitVariant: ExposureLimitVariant;
	aggregateLimit?: number;
	carriers?: CarrierPriorityInput[];
};

const headroomAmount = (used: number, limit: number | undefined): number | undefined => {
	if (limit === undefined) return undefined;
	const remaining = Currency.subtract(limit, used);
	return remaining > 0 ? remaining : undefined;
};

const overLimitAmount = (used: number, limit: number | undefined): number | undefined => {
	if (limit === undefined) return undefined;
	const overage = Currency.subtract(used, limit);
	return overage > 0 ? overage : undefined;
};

/**
 * What the account is holding: everything open plus everything approved.
 * This describes the account, not a breakdown, so it reads the same whichever tab is open --
 * the tabs re-cut one total rather than measure competing ones. Requested exposure is excluded
 * because nobody has committed to it yet.
 */
export const exposureLimitSummary = (
	exposure: ProjectedExposure,
	{ limitVariant, aggregateLimit }: BuildExposureBreakdownViewsOptions,
): ExposureLimitSummary | undefined => {
	const used = Currency.sum([
		exposure.category.bid.open,
		exposure.category.bid.approved,
		exposure.category.final.open,
		exposure.category.final.approved,
	]);
	if (aggregateLimit === undefined || aggregateLimit === 0) return undefined;

	const label = limitVariant === "admin" ? "Aggregate Limit" : "Capacity";
	const usedDisplay = formatMetricCardCurrency(used).display;
	const limitDisplay = formatMetricCardCurrency(aggregateLimit).display;
	const percent = formatToPercent(used / aggregateLimit, 0);

	return {
		label,
		value: `${usedDisplay} of ${limitDisplay} (${percent})`,
		overLimit: overLimitAmount(used, aggregateLimit) !== undefined,
	};
};

/** Unspent limit, which the bar draws to fill the track. An overage instead reddens the track. */
const headroomSegment = (
	used: number,
	aggregateLimit: number | undefined,
): ExposureSegment | undefined => {
	const headroom = headroomAmount(used, aggregateLimit);
	if (headroom === undefined) return undefined;
	return {
		id: "headroom",
		label: "Remaining",
		amount: headroom,
		color: "headroom",
	};
};

const toBreakdownView = (
	id: ExposureBreakdownView["id"],
	columns: ExposureSegmentColumn[],
	{ aggregateLimit }: BuildExposureBreakdownViewsOptions,
): ExposureBreakdownView => {
	const used = Currency.sum(
		columns.flatMap((column) => column.segments).map((segment) => segment.amount),
	);
	return {
		id,
		limit: aggregateLimit,
		overLimit: overLimitAmount(used, aggregateLimit),
		columns,
		headroom: headroomSegment(used, aggregateLimit),
		showZeroLegendRows: true,
		showTotalsColumn: true,
	};
};

/** Committed money holds one hue per type, so open and approved read as the same money. */
const COMMITTED_BID_COLOR = "primary700" as const;
const COMMITTED_FINAL_COLOR = "emerald700" as const;

/**
 * Requested money uses the approved hatch stripe as a solid: 65% white composited over the
 * committed hue, so it matches the hash without relying on overlay opacity.
 */
const REQUESTED_BID_COLOR = "primaryHatch" as const;
const REQUESTED_FINAL_COLOR = "emeraldHatch" as const;

// Hue carries the money's standing, and within a committed hue the hatch separates approved from
// open. Each open/approved/requested trio stays in its own column so the hatch reads against
// its solid.
const bidFinalColumns = (exposure: ProjectedExposure["category"]): ExposureSegmentColumn[] => [
	{
		key: "final",
		label: "Final",
		totalLabel: "",
		segments: [
			{
				id: "open-final",
				label: "Open Final",
				legendLabel: "Open",
				amount: exposure.final.open,
				color: COMMITTED_FINAL_COLOR,
				emphasis: "solid",
			},
			{
				id: "approved-final",
				label: "Approved Final",
				legendLabel: "Approved",
				amount: exposure.final.approved,
				color: COMMITTED_FINAL_COLOR,
				emphasis: "hatched",
			},
			{
				id: "requested-final",
				label: "Requested Final",
				legendLabel: "Requested",
				amount: exposure.final.requested,
				color: REQUESTED_FINAL_COLOR,
			},
		],
	},
	{
		key: "bid",
		label: "Bid",
		totalLabel: "",
		segments: [
			{
				id: "open-bid",
				label: "Open Bid",
				legendLabel: "Open",
				amount: exposure.bid.open,
				color: COMMITTED_BID_COLOR,
				emphasis: "solid",
			},
			{
				id: "approved-bid",
				label: "Approved Bid",
				legendLabel: "Approved",
				amount: exposure.bid.approved,
				color: COMMITTED_BID_COLOR,
				emphasis: "hatched",
			},
			{
				id: "requested-bid",
				label: "Requested Bid",
				legendLabel: "Requested",
				amount: exposure.bid.requested,
				color: REQUESTED_BID_COLOR,
			},
		],
	},
];

// One hue per carrier, open solid beside approved hatched. Every previewed carrier gets a
// column so an empty book still occupies its place; placed carriers the previews list does
// not rank are appended.
const carrierColumns = (
	carrier: ProjectedExposure["carrier"],
	carriers: CarrierPriorityInput[],
): ExposureSegmentColumn[] => {
	const placedByName = new Map(carrier.placed.map((placed) => [placed.carrierName, placed]));
	const seen = new Set<CarrierName>();
	const rows: { carrierName: CarrierName; open: number; approved: number }[] = [];
	for (const known of carriers) {
		if (seen.has(known.name)) continue;
		seen.add(known.name);
		const placed = placedByName.get(known.name);
		rows.push({
			carrierName: known.name,
			open: placed?.open ?? 0,
			approved: placed?.approved ?? 0,
		});
	}
	for (const placed of carrier.placed) {
		if (seen.has(placed.carrierName)) continue;
		seen.add(placed.carrierName);
		rows.push(placed);
	}

	return rows.sort(byCarrierPriority(carriers)).map((placed, index): ExposureSegmentColumn => {
		const label = carrierDisplayName[placed.carrierName] ?? placed.carrierName;
		const color = carrierSeriesColor(placed.carrierName, index);
		return {
			key: placed.carrierName,
			label,
			totalLabel: "",
			segments: [
				{
					id: `${placed.carrierName}-open`,
					label: `${label} Open`,
					legendLabel: "Open",
					amount: placed.open,
					color,
					emphasis: "solid",
				},
				{
					id: `${placed.carrierName}-approved`,
					label: `${label} Approved`,
					legendLabel: "Approved",
					amount: placed.approved,
					color,
					emphasis: "hatched",
				},
			],
		};
	});
};

export const buildExposureBreakdownViews = (
	exposure: ProjectedExposure,
	options: BuildExposureBreakdownViewsOptions,
): ExposureBreakdownView[] => [
	toBreakdownView("category", bidFinalColumns(exposure.category), options),
	toBreakdownView(
		"carrier",
		carrierColumns(exposure.carrier, options.carriers ?? EMPTY_CARRIER_PRIORITIES),
		options,
	),
];
